import React, { useState, useEffect, useCallback } from 'react';
import { TableCellsIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, ClipboardDocumentIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon, FunnelIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { adminApi, userApi } from '../services/api';
import { toast } from 'react-hot-toast';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';

// Table display configuration: preferred column order and default sorting
// Columns listed here appear first (in order), remaining columns follow
interface TableConfig {
  columns: string[];
  defaultSort: { key: string; order: 'asc' | 'desc' };
  truncate?: Record<string, number>; // column -> max chars
}

const TABLE_CONFIG: Record<string, TableConfig> = {
  // Conversations
  'conversation_history': {
    columns: ['timestamp', 'chatbot_id', 'message_type', 'content', 'session_id', 'message_sequence', 'platform', 'input_tokens', 'output_tokens', 'credits_used', 'response_time_ms'],
    defaultSort: { key: 'timestamp', order: 'desc' },
    truncate: { chatbot_id: 10, session_id: 10, conversation_id: 10, visitor_id: 10 }
  },
  'chat_sessions': {
    columns: ['last_interaction', 'session_id', 'chatbot_id', 'platform', 'total_messages', 'created_at'],
    defaultSort: { key: 'last_interaction', order: 'desc' },
    truncate: { session_id: 10, chatbot_id: 10 }
  },
  'conversation_state': {
    columns: ['updated_at', 'session_id', 'ttl'],
    defaultSort: { key: 'updated_at', order: 'desc' },
    truncate: { session_id: 10 }
  },

  // Users
  'users': {
    columns: ['email', 'name', 'user_id', 'role', 'plan', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { user_id: 10 }
  },
  'user_activity': {
    columns: ['timestamp', 'user_id', 'activity_type', 'details', 'result'],
    defaultSort: { key: 'timestamp', order: 'desc' },
    truncate: { user_id: 10 }
  },
  'user_balance': {
    columns: ['user_id', 'balance', 'updated_at'],
    defaultSort: { key: 'updated_at', order: 'desc' },
    truncate: { user_id: 10 }
  },
  'user_settings': {
    columns: ['user_id', 'theme', 'notifications'],
    defaultSort: { key: 'user_id', order: 'asc' },
    truncate: { user_id: 10 }
  },

  // Chatbots
  'chatbots': {
    columns: ['name', 'chatbot_id', 'user_id', 'model_id', 'suspend', 'created_at', 'updated_at'],
    defaultSort: { key: 'updated_at', order: 'desc' },
    truncate: { chatbot_id: 10, user_id: 10 }
  },
  'chatbot_usage': {
    columns: ['chatbot_id', 'message_count', 'last_used', 'total_tokens'],
    defaultSort: { key: 'last_used', order: 'desc' },
    truncate: { chatbot_id: 10 }
  },

  // Knowledge Base
  'knowledge_bases': {
    columns: ['kb_id', 'status', 'pool_id', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { kb_id: 10, pool_id: 10 }
  },
  'knowledge_bases_s3': {
    columns: ['kb_id', 'chatbot_id', 'status', 'document_count', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { kb_id: 10, chatbot_id: 10 }
  },
  'kb_documents': {
    columns: ['kb_id', 'document_id', 'status', 'filename', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { kb_id: 10, document_id: 10 }
  },
  'kb_sync_jobs': {
    columns: ['start_time', 'kb_id', 'job_id', 'status', 'end_time'],
    defaultSort: { key: 'start_time', order: 'desc' },
    truncate: { kb_id: 10, job_id: 10 }
  },

  // Integrations
  'discord_servers': {
    columns: ['server_id', 'user_id', 'chatbot_id', 'server_name', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { user_id: 10, chatbot_id: 10 }
  },
  'discord_oauth_tokens': {
    columns: ['user_id', 'expires_at'],
    defaultSort: { key: 'expires_at', order: 'desc' },
    truncate: { user_id: 10 }
  },
  'wordpress_integrations': {
    columns: ['site_id', 'user_id', 'chatbot_id', 'site_url', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { site_id: 10, user_id: 10, chatbot_id: 10 }
  },
  'website_integrations': {
    columns: ['last_seen', 'integration_code', 'domain', 'chatbot_id', 'user_id', 'request_count', 'first_seen'],
    defaultSort: { key: 'last_seen', order: 'desc' },
    truncate: { chatbot_id: 10, user_id: 10 }
  },
  'integration_codes': {
    columns: ['code', 'chatbot_id', 'user_id', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { chatbot_id: 10, user_id: 10 }
  },

  // Billing
  'stripe_prices': {
    columns: ['price_id', 'product_name', 'amount', 'currency', 'interval'],
    defaultSort: { key: 'amount', order: 'asc' },
    truncate: {}
  },

  // System
  'models': {
    columns: ['name', 'model_id', 'provider', 'active', 'credit_per_1k_input', 'credit_per_1k_output'],
    defaultSort: { key: 'name', order: 'asc' },
    truncate: {}
  },
  'prompts': {
    columns: ['prompt_id', 'name', 'content', 'created_at'],
    defaultSort: { key: 'prompt_id', order: 'asc' },
    truncate: {}
  },
  'settings': {
    columns: ['setting_key', 'value', 'updated_at'],
    defaultSort: { key: 'setting_key', order: 'asc' },
    truncate: {}
  },
  'pool_accounts': {
    columns: ['pool_id', 'priority', 'status', 'account_id'],
    defaultSort: { key: 'priority', order: 'asc' },
    truncate: { pool_id: 10, account_id: 10 }
  },
  'awaiting_kb_requests': {
    columns: ['request_id', 'status', 'created_at'],
    defaultSort: { key: 'created_at', order: 'desc' },
    truncate: { request_id: 10 }
  }
};

// Default config for tables not in the list
const DEFAULT_TABLE_CONFIG: TableConfig = {
  columns: [],
  defaultSort: { key: '', order: 'desc' },
  truncate: {}
};

interface TableInfo {
  name: string;
  count: number;
}

interface TableData {
  tableName: string;
  items: any[];
  count: number;
  totalCount: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

export default function Admin() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [tables, setTables] = useState<Record<string, TableInfo[]>>({});
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableData, setTableData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);

  // Sorting state
  const [sortKey, setSortKey] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Filtering state
  const [filterKey, setFilterKey] = useState<string>('');
  const [filterValue, setFilterValue] = useState<string>('');
  const [filterInput, setFilterInput] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await userApi.getProfile();
      if (response.success && response.data?.role === 'admin') {
        setIsAdmin(true);
        loadTables();
      } else {
        setIsAdmin(false);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      setIsAdmin(false);
      setLoading(false);
    }
  };

  const loadTables = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getTables();
      if (response.success && response.data) {
        setTables(response.data);
        const categories = Object.keys(response.data);
        if (categories.length > 0) {
          setActiveCategory(categories[0]);
          const firstTable = response.data[categories[0]]?.[0];
          if (firstTable) {
            loadTableData(firstTable.name, true);
          }
        }
      } else {
        toast.error('Failed to load tables');
      }
    } catch (error: any) {
      console.error('Error loading tables:', error);
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        toast.error('Failed to load tables');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTableData = useCallback(async (
    tableName: string,
    isNewTable: boolean = false,
    page: number = 1,
    sort?: { key: string; order: 'asc' | 'desc' },
    filter?: { key: string; value: string } | null  // null = explicitly clear filter
  ) => {
    try {
      setTableLoading(true);

      // Get default sort from config for new tables
      const tableConfig = TABLE_CONFIG[tableName] || DEFAULT_TABLE_CONFIG;
      const defaultSort = tableConfig.defaultSort;

      if (isNewTable) {
        setSortKey(defaultSort.key);
        setSortOrder(defaultSort.order);
        setFilterKey('');
        setFilterValue('');
        setFilterInput('');
        setCurrentPage(1);
        page = 1;
      }

      // Determine sort to use
      const effectiveSort = sort || (isNewTable ? defaultSort : { key: sortKey, order: sortOrder });

      // Determine filter: null = clear, undefined = use state, object = use passed value
      const effectiveFilterKey = filter === null ? undefined : (filter?.key || (isNewTable ? undefined : filterKey) || undefined);
      const effectiveFilterValue = filter === null ? undefined : (filter?.value || (isNewTable ? undefined : filterValue) || undefined);

      const response = await adminApi.getTableData(tableName, {
        limit: 20,
        page,
        sortKey: effectiveSort.key || undefined,
        sortOrder: effectiveSort.key ? effectiveSort.order : undefined,
        filterKey: effectiveFilterKey,
        filterValue: effectiveFilterValue,
      });

      if (response.success && response.data) {
        setTableData(response.data);
        setSelectedTable(tableName);
        setCurrentPage(response.data.page);
      } else {
        toast.error('Failed to load table data');
      }
    } catch (error) {
      console.error('Error loading table data:', error);
      toast.error('Failed to load table data');
    } finally {
      setTableLoading(false);
    }
  }, [sortKey, sortOrder, filterKey, filterValue]);

  const handleSort = (column: string) => {
    const newOrder = sortKey === column && sortOrder === 'desc' ? 'asc' : 'desc';
    setSortKey(column);
    setSortOrder(newOrder);
    loadTableData(selectedTable, false, 1, { key: column, order: newOrder }, filterKey ? { key: filterKey, value: filterValue } : undefined);
  };

  const handleFilter = () => {
    if (filterKey && filterInput) {
      setFilterValue(filterInput);
      setCurrentPage(1);
      loadTableData(selectedTable, false, 1, sortKey ? { key: sortKey, order: sortOrder } : undefined, { key: filterKey, value: filterInput });
    }
  };

  const clearFilter = () => {
    setFilterKey('');
    setFilterValue('');
    setFilterInput('');
    setCurrentPage(1);
    loadTableData(selectedTable, false, 1, sortKey ? { key: sortKey, order: sortOrder } : undefined, null);  // null = clear filter
  };

  const applyFilterFromModal = (key: string, value: any) => {
    // Convert value to string for filtering
    const stringValue = value === null || value === undefined ? '' :
      typeof value === 'object' ? JSON.stringify(value) : String(value);

    if (!stringValue) {
      toast.error('Cannot filter on empty value');
      return;
    }

    setFilterKey(key);
    setFilterValue(stringValue);
    setFilterInput(stringValue);
    setCurrentPage(1);
    setSelectedRow(null); // Close modal
    loadTableData(selectedTable, false, 1, sortKey ? { key: sortKey, order: sortOrder } : undefined, { key, value: stringValue });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadTableData(selectedTable, false, page);
  };

  const handleRefresh = () => {
    if (selectedTable) {
      loadTableData(
        selectedTable,
        false,
        currentPage,
        sortKey ? { key: sortKey, order: sortOrder } : undefined,
        filterKey ? { key: filterKey, value: filterValue } : undefined
      );
    }
  };

  const formatValue = (value: any, column?: string, tableName?: string): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }

    let strValue = String(value);

    // Apply truncation if configured
    if (column && tableName) {
      const config = TABLE_CONFIG[tableName];
      const truncateLen = config?.truncate?.[column];
      if (truncateLen && strValue.length > truncateLen) {
        strValue = strValue.substring(0, truncateLen) + '...';
      }
    }

    return strValue;
  };

  // Get columns in preferred order: configured columns first, then remaining
  // When no items (e.g., filter returned no results), return configured columns
  const getOrderedColumns = (tableName: string, items: any[]): string[] => {
    const config = TABLE_CONFIG[tableName] || DEFAULT_TABLE_CONFIG;

    // If no items, return configured columns so filter UI stays visible
    if (items.length === 0) {
      return config.columns;
    }

    const allKeys = new Set<string>();
    items.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });

    const preferredColumns = config.columns.filter(col => allKeys.has(col));
    const remainingColumns = Array.from(allKeys).filter(col => !config.columns.includes(col));

    return [...preferredColumns, ...remainingColumns];
  };

  // Get table config helper
  const getTableConfig = (tableName: string): TableConfig => {
    return TABLE_CONFIG[tableName] || DEFAULT_TABLE_CONFIG;
  };

  const formatValueForModal = (value: any): string => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2);
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  const copyToClipboard = () => {
    if (selectedRow) {
      navigator.clipboard.writeText(JSON.stringify(selectedRow, null, 2));
      toast.success('Copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAdmin === false) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <TableCellsIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-gray-100">Access Denied</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            You do not have admin access to view this page.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const categories = Object.keys(tables);
  const columns = tableData?.items && selectedTable ? getOrderedColumns(selectedTable, tableData.items) : [];

  return (
    <div className="space-y-6">
      {/* Category Tabs */}
      <div className="border-b border-gray-200 dark:border-slate-700">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setActiveCategory(category);
                const firstTable = tables[category]?.[0];
                if (firstTable) {
                  loadTableData(firstTable.name, true);
                } else {
                  setSelectedTable('');
                  setTableData(null);
                }
              }}
              className={clsx(
                'whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm',
                activeCategory === category
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              )}
            >
              {category}
            </button>
          ))}
        </nav>
      </div>

      {/* Table Tabs (Secondary Menu) */}
      <div className="flex flex-wrap gap-2">
        {tables[activeCategory]?.map((table) => (
          <button
            key={table.name}
            onClick={() => loadTableData(table.name, true)}
            className={clsx(
              'px-3 py-1.5 text-sm rounded-md transition-colors',
              selectedTable === table.name
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
            )}
          >
            {table.name}
          </button>
        ))}
        {(!tables[activeCategory] || tables[activeCategory].length === 0) && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            No tables in this category
          </span>
        )}
      </div>

      {/* Table Data */}
      <div>
        <div className="bg-white dark:bg-slate-800 shadow rounded-lg border border-gray-200 dark:border-slate-700">
          {!selectedTable ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <TableCellsIcon className="mx-auto h-12 w-12 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Select a table to view its data
                </p>
              </div>
            </div>
          ) : tableLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-6 h-6 border border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
          ) : tableData ? (
            <>
              <div className="px-4 py-2 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between gap-4 flex-wrap">
                {/* Left: Row count and filter controls */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {tableData.count} of {tableData.totalCount} items
                  </span>
                  <div className="flex items-center gap-2">
                    <select
                      value={filterKey}
                      onChange={(e) => setFilterKey(e.target.value)}
                      className="text-xs border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Filter by...</option>
                      {columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={filterInput}
                      onChange={(e) => setFilterInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleFilter()}
                      placeholder="Value..."
                      disabled={!filterKey}
                      className="text-xs border border-gray-300 dark:border-slate-600 rounded px-2 py-1 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 disabled:opacity-50 w-64"
                    />
                    <button
                      onClick={handleFilter}
                      disabled={!filterKey || !filterInput}
                      className="p-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                    </button>
                    {filterValue && (
                      <button
                        onClick={clearFilter}
                        className="px-2 py-1 text-xs text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-slate-600 hover:bg-gray-200 dark:hover:bg-slate-500 rounded flex items-center gap-1"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                {/* Right: Refresh + Pagination */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleRefresh}
                    disabled={tableLoading}
                    className={clsx(
                      'p-1 rounded',
                      tableLoading
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    )}
                    title="Refresh"
                  >
                    <ArrowPathIcon className={clsx('h-5 w-5', tableLoading && 'animate-spin')} />
                  </button>
                  <div className="w-px h-5 bg-gray-200 dark:bg-slate-600" />
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className={clsx(
                      'p-1 rounded',
                      currentPage <= 1
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    )}
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Page {tableData.page} of {tableData.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!tableData.hasMore}
                    className={clsx(
                      'p-1 rounded',
                      !tableData.hasMore
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'
                    )}
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                {tableData.items.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No items in this table</p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                    <thead className="bg-gray-50 dark:bg-slate-700">
                      <tr>
                        {columns.map((key) => (
                          <th
                            key={key}
                            onClick={() => handleSort(key)}
                            className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-600 select-none"
                          >
                            <div className="flex items-center gap-1">
                              {key}
                              {sortKey === key && (
                                sortOrder === 'desc'
                                  ? <ChevronDownIcon className="h-3 w-3" />
                                  : <ChevronUpIcon className="h-3 w-3" />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                      {tableData.items.map((item, index) => (
                        <tr
                          key={index}
                          className="hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer"
                          onClick={() => setSelectedRow(item)}
                        >
                          {columns.map((key) => (
                            <td
                              key={key}
                              className="px-2 py-1 text-xs text-gray-900 dark:text-gray-100 max-w-[200px] truncate"
                              title={formatValue(item[key])}
                            >
                              {formatValue(item[key], key, selectedTable)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Row Detail Modal */}
      {selectedRow && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedRow(null)}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Row Details
              </h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={copyToClipboard}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                  title="Copy JSON"
                >
                  <ClipboardDocumentIcon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setSelectedRow(null)}
                  className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            {/* Modal Body */}
            <div className="px-4 py-3 overflow-y-auto max-h-[calc(80vh-60px)]">
              <dl className="space-y-3">
                {Object.entries(selectedRow).map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-2 group">
                    <dt className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase break-all flex items-start gap-1">
                      <button
                        onClick={() => applyFilterFromModal(key, value)}
                        className="p-0.5 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-slate-700 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                        title={`Filter by ${key}`}
                      >
                        <FunnelIcon className="h-3 w-3" />
                      </button>
                      {key}
                    </dt>
                    <dd className="col-span-2 text-xs text-gray-900 dark:text-gray-100">
                      {typeof value === 'object' && value !== null ? (
                        <pre className="bg-gray-50 dark:bg-slate-900 p-2 rounded text-[11px] overflow-x-auto whitespace-pre-wrap break-all">
                          {formatValueForModal(value)}
                        </pre>
                      ) : (
                        <span className="break-all">{formatValueForModal(value)}</span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
